import React from "react";
import axios from "axios";
import { Upload, Trash2, X, Save } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectApprovalComplianceForm = ({
  approvalData,
  setApprovalData,
  onSave,
}) => {
  const documents = approvalData.approvals_compliances || [];

  // Clear only approvals & compliances
  const clearDocuments = () => {
    setApprovalData((prev) => ({
      ...prev,
      approvals_compliances: [],
    }));
  };

  // Handle PDF upload
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const pdfFiles = files.filter((file) => file.type === "application/pdf");

    if (!pdfFiles.length) {
      alert("Please upload only PDF files.");
      return;
    }

    const token = localStorage.getItem("token");
    const formData = new FormData();
    pdfFiles.forEach((file) => formData.append("media", file)); // ✅ matches upload-media field

    try {
      // ✅ Use existing backend upload route
      const res = await axios.post(
        `${API_BASE_URL}/admin/upload-media`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // ✅ Adjust response handling
      const uploaded =
        res.data?.data?.files?.map((f) => f.url) ||
        res.data?.files ||
        res.data?.urls ||
        [];

      if (!uploaded.length) {
        console.error("Upload response missing file URLs:", res.data);
        alert("Upload completed but no file URLs returned. Check backend.");
        return;
      }

      setApprovalData((prev) => ({
        ...prev,
        approvals_compliances: [
          ...(prev.approvals_compliances || []),
          ...uploaded,
        ],
      }));
    } catch (error) {
      console.error("Error uploading approval PDFs:", error);
      alert("Failed to upload PDFs. Check console for details.");
    }
  };

  const handleRemove = (index) => {
    setApprovalData((prev) => {
      const updated = [...(prev.approvals_compliances || [])];
      updated.splice(index, 1);
      return { ...prev, approvals_compliances: updated };
    });
  };

  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-semibold text-blue-700">
          Approvals & Compliances
        </h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={clearDocuments}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} />
            Clear
          </button>
          <button
            type="button"
            onClick={onSave}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <Save size={18} />
            Save
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Upload RERA certificates, HMDA approvals, sanction letters, compliance
        reports, or any other mandatory documents for this project.
      </p>

      {/* Upload Area */}
      <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center gap-3">
        <Upload size={32} className="text-blue-500" />
        <p className="text-sm text-gray-700">
          Select one or more PDF files to attach to this project record.
        </p>
        <label className="cursor-pointer inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-100">
          Choose PDFs
          <input
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
      </div>

      {/* List of uploaded PDFs */}
      {documents.length > 0 && (
        <div className="mt-2">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            Uploaded documents
          </h3>
          <ul className="space-y-2">
            {documents.map((doc, index) => {
              const isString = typeof doc === "string";
              const label = isString
                ? doc.split("/").pop()
                : doc.name || `Document ${index + 1}`;
              const href = isString ? doc : "#";

              return (
                <li
                  key={index}
                  className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs sm:text-sm"
                >
                  <a
                    href={href}
                    target={isString ? "_blank" : undefined}
                    rel={isString ? "noopener noreferrer" : undefined}
                    className={
                      isString
                        ? "truncate text-blue-600 hover:underline"
                        : "truncate text-gray-800"
                    }
                  >
                    {label}
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="p-1 rounded-full hover:bg-red-50 text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ProjectApprovalComplianceForm;
